import styled from 'styled-components';

import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconApi } from '@components/icons/api';
import { IconForm } from '@components/icons/form';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconTrigger } from '@components/icons/trigger';
import { IconWebhook } from '@components/icons/webhook';
import { InfoBox } from '@components/info-box';
import { ListCard } from '@components/list-card';
import { forceHideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCreateJourneyWebhook } from 'api/journey-webhooks/hooks';

import { useScheduledPollingSidebar } from './scheduled-polling';
import { useWebhookSidebar } from './webhook-sidebar';

import { useEditJourneyFormOverlay } from '../journey-form/edit-journey-form-overlay';
import { useCreateDefaultJourneyForm } from '../journey-form/use-create-default-journey-form';

export const useAddTravellersSidebar = () => {
  const sidebar = useSidebar(AddTravellersSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type AddTravellersSidebarProps = {
  journeyShortId: string;
};

export const AddTravellersSidebar = ({ journeyShortId }: AddTravellersSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { createDefault } = useCreateDefaultJourneyForm({ journeyShortId });
  const showWebhookSidebar = useWebhookSidebar();
  const showScheduledPollingSidebar = useScheduledPollingSidebar();
  const showEditJourneyFormOverlay = useEditJourneyFormOverlay();

  const createWebhookMutation = useCreateJourneyWebhook(journeyShortId);

  const handleCreateWebhook = () => {
    createWebhookMutation.mutate(undefined, {
      onSuccess: (data) => {
        showWebhookSidebar({ webhookShortId: data.shortId, journeyShortId });
      },
    });
  };

  const handleCreateJourneyForm = async () => {
    const formShortId = await createDefault();
    showEditJourneyFormOverlay({
      journeyId: journeyShortId,
      formId: formShortId ?? '',
    });
    forceHideSidebar();
  };

  const advancedCards = [
    {
      Icon: (
        <IconFancyContainer
          variant="orange"
          hasBorder
          icon={<IconWebhook color="elementOrange100" />}
        />
      ),
      title: t('webhook'),
      description: t('webhookDescription'),
      onClick: handleCreateWebhook,
    },
    {
      Icon: (
        <IconFancyContainer
          variant="indigo"
          hasBorder
          icon={<IconApi color="elementIndigo100" />}
        />
      ),
      title: t('scheduledPolling'),
      description: t('scheduledPollingDescription'),
      onClick: () => showScheduledPollingSidebar({ journeyShortId }),
    },
  ];

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconTrigger}
        iconColor="elementBlue100"
        title={t('addTravellers')}
      />
      <Sidebar.Content>
        <Stack space={4}>
          <Text font="regular14" color="textSecondary" text={t('description')} />
          <HoverableListCard
            icon={
              <IconFancyContainer
                variant="green"
                hasBorder
                icon={<IconForm color="elementGreen200" />}
              />
            }
            title={t('externalForm')}
            description={t('externalFormDescription')}
            onClick={handleCreateJourneyForm}
            disableEllipsis
          />

          <Stack space={2}>
            <Text font="semibold14" text={t('advancedOption')} />
            {advancedCards.map((card, idx) => (
              <HoverableListCard
                key={idx}
                icon={card.Icon}
                title={card.title}
                description={card.description}
                onClick={card.onClick}
                disableEllipsis
              />
            ))}
          </Stack>
          <InfoBox
            text={t('triggerInfo')}
            variant="indigo"
            icon={<IconFormIndicatorInfo color="elementIndigo100" />}
          />
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const HoverableListCard = styled(ListCard)({
  padding: pxToRem(12),
  minHeight: pxToRem(76),
  transition: 'background-color 150ms ease',
  '&:hover': {
    backgroundColor: theme.color.backgroundLightDarkHover,
  },
});

export const locale = {
  en: {
    addTravellers: 'Add journey trigger',
    description:
      'Triggers are how people or systems start your workflow. Choose the methods that work best for your use case',
    externalForm: 'External form',
    externalFormDescription:
      'Create forms that people can fill out to join this Journey\n\n• Display on your company portal\n• Share as a link',
    advancedOption: 'Advanced Options',
    webhook: 'Webhook',
    webhookDescription: 'Create and start travellers through custom HTTP requests',
    scheduledPolling: 'Scheduled Polling',
    scheduledPollingDescription: 'Scheduled checks at intervals with external data or systems',
    triggerInfo:
      'What happens when your trigger activates?\n\nA new person will be added to this workflow and receive the sequence of messages, tasks, or actions you set up',
  },
};
